import React from 'react'
import PropTypes from 'prop-types'
import { Mutation, useMutation } from 'react-apollo'

import {
  EDIT_ITEM_IN_CART,
  ADD_ITEM_TO_CART,
  ADD_ITEM_TO_CART_AND_UPDATE_DINING_OPTION,
  DELETE_ITEM_FROM_CART,
  EDIT_QUANTITY_OF_ITEM_IN_CART,
  GET_CART
} from '../../apollo/cart/cart.graphql'

import { CartConsumer, useCart } from '../CartProvider/CartProvider'
import { dataByTypename } from '../../utils/apollo-helpers'
import { useOutpost } from '../OutpostProvider/OutpostProvider'
import { CLEAR_MODIFICATION_ERRORS } from '../CartProvider/cart-actions'

const nop = () => {}

const updateCache = getResponse => (cache, { data }) => {
  const response = getResponse(data)
  const { CartResponse, CartOutOfStockError } = dataByTypename(response)

  if (CartResponse || CartOutOfStockError) {
    const guid = CartResponse ? CartResponse.cart.guid : CartOutOfStockError.cart.guid
    cache.writeQuery({
      query: GET_CART,
      data: {
        cartV2: {
          info: [],
          warnings: [],
          ...response,
          __typename: 'CartResponse'
        }
      },
      variables: { guid }
    })
  }
}

export const CartMutation = ({ mutation, onCompleted = nop, onError, children }) => (
  <CartConsumer>
    {cartProps => (
      <Mutation mutation={mutation} onError={onError} onCompleted={data => onCompleted(data, cartProps)}>
        {(mutate, result) => children(mutate, result, cartProps)}
      </Mutation>
    )}
  </CartConsumer>
)

CartMutation.propTypes = {
  mutation: PropTypes.object.isRequired,
  onCompleted: PropTypes.func,
  onError: PropTypes.func,
  children: PropTypes.func.isRequired
}

export const AddItemToCartMutation = ({ children, onCompleted = nop, onError }) => (
  <CartMutation
    mutation={ADD_ITEM_TO_CART}
    onError={onError}
    update={updateCache(data => data.addItemToCartV2)}
    onCompleted={(data, cartProps) => {
      const { CartResponse, CartOutOfStockError, CartModificationError } = dataByTypename(data.addItemToCartV2)
      if (CartResponse || CartOutOfStockError) {
        const cartGuid = CartResponse ? CartResponse.cart.guid : CartOutOfStockError.cart.guid
        cartProps.updateCartGuid(cartGuid)
        onCompleted(data, cartProps)
      }
      if (CartModificationError) {
        throw new Error(CartModificationError.message)
      }
    }}
    children={children}
  />
)

export const useAddItemToCart = ({onCompleted = nop, onError} = {}) => {
  const cartProps = useCart()
  const { isOutpost } = useOutpost()
  let query = ADD_ITEM_TO_CART
  if (isOutpost) {
    query = ADD_ITEM_TO_CART_AND_UPDATE_DINING_OPTION
  }
  return useMutation(query, {
    onError,
    update: updateCache(data => data.addItemToCartV2),
    onCompleted: data => {
      const {CartResponse, CartOutOfStockError, CartModificationError} = dataByTypename(data.addItemToCartAndUpdateDiningOption || data.addItemToCartV2)
      if (CartResponse || CartOutOfStockError) {
        const cartGuid = CartResponse ? CartResponse.cart.guid : CartOutOfStockError.cart.guid
        cartProps.updateCartGuid(cartGuid)
        cartProps.dispatch({ type: CLEAR_MODIFICATION_ERRORS })
        onCompleted(data, cartProps)
      }
      if (CartModificationError) {
        throw new Error(CartModificationError.message)
      }
    }
  })
}

AddItemToCartMutation.propTypes = {
  onCompleted: PropTypes.func,
  onError: PropTypes.func,
  children: PropTypes.func.isRequired
}

export const EditItemInCartMutation = ({ children, onCompleted = nop, onError }) => (
  <CartMutation
    mutation={EDIT_ITEM_IN_CART}
    onError={onError}
    update={updateCache(data => data.editItemInCartV2)}
    onCompleted={(data, cartProps) => {
      const { CartResponse, CartOutOfStockError, CartModificationError } = dataByTypename(data.editItemInCartV2)
      if (CartResponse || CartOutOfStockError) {
        const cartGuid = CartResponse ? CartResponse.cart.guid : CartOutOfStockError.cart.guid
        cartProps.updateCartGuid(cartGuid)
        onCompleted(data, cartProps)
      }
      if (CartModificationError) {
        throw new Error(CartModificationError.message)
      }
    }}
    children={children}
  />
)

EditItemInCartMutation.propTypes = {
  onCompleted: PropTypes.func,
  onError: PropTypes.func,
  children: PropTypes.func.isRequired
}

export const useEditItemInCart = ({onCompleted = nop, onError} = {}) => {
  const cartProps = useCart()
  return useMutation(EDIT_ITEM_IN_CART, {
    onError,
    update: updateCache(data => data.editItemInCartV2),
    onCompleted: data => {
      const { CartResponse, CartOutOfStockError, CartModificationError } = dataByTypename(data.editItemInCartV2)
      if (CartResponse || CartOutOfStockError) {
        const cartGuid = CartResponse ? CartResponse.cart.guid : CartOutOfStockError.cart.guid
        cartProps.updateCartGuid(cartGuid)
        cartProps.dispatch({ type: CLEAR_MODIFICATION_ERRORS })
        onCompleted(data, cartProps)
      }
      if (CartModificationError) {
        throw new Error(CartModificationError.message)
      }
    }
  })
}

export const DeleteItemFromCartMutation = ({ children, onCompleted, onError }) => (
  <CartMutation
    mutation={DELETE_ITEM_FROM_CART}
    onError={onError}
    update={updateCache(data => data.deleteItemFromCartV2)}
    onCompleted={onCompleted}
    children={children} />
)

export const useDeleteItemFromCart = ({onCompleted = nop, onError, ...args} = {}) => {
  return useMutation(DELETE_ITEM_FROM_CART, {
    onError,
    update: updateCache(data => data.deleteItemFromCartV2),
    onCompleted: data => {
      const { CartResponse, CartOutOfStockError, CartModificationError } = dataByTypename(data.deleteItemFromCartV2)
      if (CartResponse || CartOutOfStockError) {
        onCompleted(data)
      }
      if (CartModificationError) {
        throw new Error(CartModificationError.message)
      }
    },
    ...args
  })
}

DeleteItemFromCartMutation.propTypes = {
  onCompleted: PropTypes.func,
  onError: PropTypes.func,
  children: PropTypes.func.isRequired
}

export const EditQuantityOfItemInCartMutation = ({ children, onCompleted, onError }) => (
  <CartMutation mutation={EDIT_QUANTITY_OF_ITEM_IN_CART} onError={onError} onCompleted={onCompleted} children={children} />
)

EditQuantityOfItemInCartMutation.propTypes = {
  onCompleted: PropTypes.func,
  onError: PropTypes.func,
  children: PropTypes.func.isRequired
}
